import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ExternalLink } from 'lucide-react';
import { SettingsSection } from './SettingsLayout';

export default function IntegrationsSettings() {
    return (
        <div>
            <MsGraphCard />
            <MonzoCard />
            <LeadGenKeysCard />
        </div>
    );
}

function MonzoCard() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const r = await api.get('/auth/monzo/status'); setStatus(r.data); }
        catch { /* non-admin */ } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('monzo') === 'connected') { load(); }
        if (params.get('monzo_error')) {
            toast.error(`Monzo connection failed: ${params.get('monzo_error').replace(/_/g,' ')}`);
        }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const connect = () => { window.location.href = `${api.defaults.baseURL}/auth/monzo/start`; };
    const disconnect = async () => {
        if (!window.confirm('Disconnect Monzo? Transaction sync will stop until reconnected.')) return;
        try { await api.post('/auth/monzo/disconnect'); toast.success('Disconnected'); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };
    const sync = async () => {
        setSyncing(true);
        try { const r = await api.post('/monzo/sync'); toast.success(`Synced — ${r.data.stored ?? r.data.fetched} transactions stored`); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Sync failed'); }
        finally { setSyncing(false); }
    };

    return (
        <SettingsSection title="Monzo Business" wide testId="monzo-card">
            <div className="sm:col-span-2">
                {loading && <div className="text-[12.5px] text-[color:var(--bm-text-muted)]">Loading…</div>}
                {!loading && status && !status.configured && (
                    <div className="text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        Monzo is not configured. Add <code>MONZO_CLIENT_ID</code>, <code>MONZO_CLIENT_SECRET</code>, <code>MONZO_REDIRECT_URI</code> and <code>MONZO_ENABLED=true</code> to the backend env.
                    </div>
                )}
                {!loading && status && status.configured && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div className="text-[11.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Status</div>
                                <div className="text-[14px] font-semibold mt-0.5">
                                    {status.connected
                                        ? <span className="text-emerald-700" data-testid="monzo-connected">✓ Connected · {status.account_type || 'Business account'}</span>
                                        : <span className="text-[color:var(--bm-text-muted)]" data-testid="monzo-disconnected">Not connected</span>}
                                </div>
                                {status.connected_at && <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-0.5">Since {new Date(status.connected_at).toLocaleString('en-GB')}</div>}
                                {status.last_sync_at && <div className="text-[11px] text-[color:var(--bm-text-subtle)]">Last sync {new Date(status.last_sync_at).toLocaleString('en-GB')}</div>}
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {!status.connected
                                    ? <Button onClick={connect} className="h-9" style={{ background: '#FF3B30' }} data-testid="monzo-connect-btn">Connect Monzo Business</Button>
                                    : <>
                                        <Button onClick={sync} disabled={syncing} variant="outline" className="h-9" data-testid="monzo-sync-btn">{syncing ? 'Syncing…' : 'Sync now'}</Button>
                                        <Button onClick={connect} variant="outline" className="h-9" data-testid="monzo-reconnect-btn">Reconnect</Button>
                                        <Button onClick={disconnect} variant="outline" className="h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="monzo-disconnect-btn">Disconnect</Button>
                                    </>}
                            </div>
                        </div>
                        {status.connected && (
                            <div className="text-[12px] text-[color:var(--bm-text-muted)] bg-[color:var(--bm-bg-mute)] rounded-lg px-3 py-2.5 flex items-center gap-2">
                                <ExternalLink size={12} />
                                Transactions sync automatically via webhook. Incoming credits are matched against outstanding invoices by reference or exact amount.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </SettingsSection>
    );
}

function MsGraphCard() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testEmail, setTestEmail] = useState('');
    const [sending, setSending] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/auth/microsoft/status');
            setStatus(r.data);
            if (!testEmail && r.data.ms_email) setTestEmail(r.data.ms_email);
        } catch { /* non-admin */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const connect = () => { window.location.href = `${api.defaults.baseURL}/auth/microsoft/start`; };
    const disconnect = async () => {
        if (!window.confirm('Disconnect Microsoft account? Emails will stop sending until reconnected.')) return;
        try { await api.post('/auth/microsoft/disconnect'); toast.success('Disconnected'); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };
    const sendTest = async () => {
        if (!testEmail) return toast.error('Enter a recipient');
        setSending(true);
        try {
            await api.post('/auth/microsoft/test-email', { to: testEmail });
            toast.success(`Test email sent to ${testEmail}`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Send failed'); }
        finally { setSending(false); }
    };

    return (
        <SettingsSection title="Microsoft 365 connection" wide testId="ms-graph-card">
            <div className="sm:col-span-2">
                {loading && <div className="text-[12.5px] text-[color:var(--bm-text-muted)]">Loading…</div>}
                {!loading && status && !status.configured && (
                    <div className="text-[12.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-3 space-y-2" data-testid="ms-graph-not-configured">
                        <div className="font-semibold">Microsoft OAuth is not configured on this deployment.</div>
                        {Array.isArray(status.missing_env_vars) && status.missing_env_vars.length > 0 && (
                            <div>
                                <div className="text-[11.5px]">Missing or empty env vars on the backend:</div>
                                <ul className="mt-1 ml-4 list-disc font-mono text-[11.5px]">
                                    {status.missing_env_vars.map(v => <li key={v}>{v}</li>)}
                                </ul>
                            </div>
                        )}
                        <div className="text-[11.5px]">
                            Required env vars: <code>AZURE_TENANT_ID</code>, <code>AZURE_CLIENT_ID</code>, <code>AZURE_CLIENT_SECRET</code>, <code>AZURE_REDIRECT_URI</code>, <code>MS_TOKEN_ENCRYPTION_KEY</code>.
                        </div>
                        {status.redirect_uri && (
                            <div>
                                <div className="text-[11.5px] mt-2">Redirect URI registered on this server (must match Azure AD app exactly):</div>
                                <code className="block mt-1 px-2 py-1 bg-white border border-amber-200 rounded text-[11.5px] break-all" data-testid="ms-graph-redirect-uri">{status.redirect_uri}</code>
                            </div>
                        )}
                    </div>
                )}
                {!loading && status && status.configured && (
                    <div className="space-y-4">
                        <div className="text-[11px] text-[color:var(--bm-text-subtle)] bg-[color:var(--bm-bg-mute)] border border-[color:var(--bm-border)] rounded px-3 py-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="ms-graph-config">
                            {status.redirect_uri && <div className="break-all"><span className="font-semibold text-[color:var(--bm-text)]">Redirect URI:</span> <code>{status.redirect_uri}</code></div>}
                            {status.post_login_url && <div className="break-all"><span className="font-semibold text-[color:var(--bm-text)]">Post-login:</span> <code>{status.post_login_url}</code></div>}
                            {(status.tenant_id_suffix || status.client_id_suffix) && <div className="sm:col-span-2"><span className="font-semibold text-[color:var(--bm-text)]">App registration:</span> tenant ends <code>…{status.tenant_id_suffix}</code> · client ends <code>…{status.client_id_suffix}</code></div>}
                        </div>
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div className="text-[11.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Status</div>
                                <div className="text-[14px] font-semibold mt-0.5">
                                    {status.connected
                                        ? <span className="text-emerald-700" data-testid="ms-graph-connected">✓ Connected as {status.ms_email}</span>
                                        : <span className="text-[color:var(--bm-text-muted)]" data-testid="ms-graph-disconnected">Not connected</span>}
                                </div>
                                {status.connected_at && <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-0.5">Since {new Date(status.connected_at).toLocaleString('en-GB')}</div>}
                            </div>
                            <div className="flex gap-2">
                                {!status.connected
                                    ? <Button onClick={connect} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="ms-graph-connect-btn">Connect Microsoft account</Button>
                                    : <>
                                        <Button onClick={connect} variant="outline" className="h-9" data-testid="ms-graph-reconnect-btn">Reconnect</Button>
                                        <Button onClick={disconnect} variant="outline" className="h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="ms-graph-disconnect-btn">Disconnect</Button>
                                    </>}
                            </div>
                        </div>
                        {status.connected && (
                            <div className="pt-4 border-t border-[color:var(--bm-border)]">
                                <div className="text-[11.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-2">Send a test email</div>
                                <div className="flex gap-2">
                                    <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@example.com" className="h-9 flex-1" data-testid="ms-graph-test-input" />
                                    <Button onClick={sendTest} disabled={sending} className="h-9" style={{ background: 'var(--bm-accent-600)' }} data-testid="ms-graph-test-btn">
                                        {sending ? 'Sending…' : 'Send test'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </SettingsSection>
    );
}

function LeadGenKeysCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ outscraper_api_key: '', hunter_api_key: '', resend_api_key: '', sender_email: '', sender_name: '' });

    const load = async () => {
        setLoading(true);
        try { const r = await api.get('/leadgen/settings'); setData(r.data); }
        catch { /* non-admin */ } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const save = async () => {
        const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '' && v !== null && v !== undefined));
        if (Object.keys(payload).length === 0) {
            toast.error('Nothing to save — fill in a field first');
            return;
        }
        setSaving(true);
        try {
            await api.put('/leadgen/settings', payload);
            toast.success('LeadGen settings updated');
            setForm({ outscraper_api_key: '', hunter_api_key: '', resend_api_key: '', sender_email: '', sender_name: '' });
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Save failed');
        } finally { setSaving(false); }
    };

    return (
        <SettingsSection title="Lead Gen — API keys & sender" wide testId="leadgen-keys-card">
            <div className="sm:col-span-2">
                {loading && <div className="text-[12.5px] text-[color:var(--bm-text-muted)]">Loading…</div>}
                {!loading && data && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <KeyField label="Outscraper API key" stored={data.outscraper_api_key} isSet={data.has_outscraper}
                                helper="Used to scrape leads from Google Maps." value={form.outscraper_api_key}
                                onChange={(v) => update('outscraper_api_key', v)} testId="leadgen-outscraper-input" />
                            <KeyField label="Hunter.io API key" stored={data.hunter_api_key} isSet={data.has_hunter}
                                helper="Used to verify lead emails." value={form.hunter_api_key}
                                onChange={(v) => update('hunter_api_key', v)} testId="leadgen-hunter-input" />
                            <KeyField label="Resend API key" stored={data.resend_api_key} isSet={data.has_resend}
                                helper="Used to send outreach email campaigns." value={form.resend_api_key}
                                onChange={(v) => update('resend_api_key', v)} testId="leadgen-resend-input" />
                            <div>
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Sender email</Label>
                                <Input value={form.sender_email || data.sender_email || ''} placeholder="fabio@beyondmeasure.global"
                                    onChange={(e) => update('sender_email', e.target.value)} className="mt-1.5 h-10" data-testid="leadgen-sender-email" />
                            </div>
                            <div>
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Sender name</Label>
                                <Input value={form.sender_name || data.sender_name || ''} placeholder="Beyond Measure"
                                    onChange={(e) => update('sender_name', e.target.value)} className="mt-1.5 h-10" data-testid="leadgen-sender-name" />
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={save} disabled={saving} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="leadgen-keys-save-btn">
                                {saving ? 'Saving…' : 'Save LeadGen keys'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </SettingsSection>
    );
}

function KeyField({ label, stored, isSet, helper, value, onChange, testId }) {
    return (
        <div>
            <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">{label}</Label>
            <Input type="password" value={value || ''} placeholder={isSet ? `Stored: ${stored}` : 'Not configured'}
                onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-10" data-testid={testId} />
            <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-1">
                {isSet ? <span className="text-emerald-600 font-medium">✓ Set.</span> : <span className="text-amber-600 font-medium">Not set.</span>} {helper}
            </div>
        </div>
    );
}
